import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePageTitle } from '../../lib/hooks/usePageTitle.js';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { api } from '../../lib/api.js';
import { formatCurrency, formatDate } from '../../lib/formatters.js';
import { SpeedKpiStrip } from '../../components/dashboard/SpeedKpiStrip.js';
import { PriorityBadge } from './components/PriorityBadge.js';

interface KpiData {
  minutesSaved: number;
  fteProjected: number;
  awaitingReview: number;
  autoApprovedToday: number;
  settledThisWeek: number;
  awaitingExternalVerification: number;
}

interface QueueItem {
  id: string;
  claimNumber: string;
  claimType: string;
  status: string;
  claimedAmount: string;
  isoCurrencyCode: string;
  verificationConfidence: string | null;
  submittedAt: string | null;
  distributorName: string;
  aiVerdict: string | null;
  priorityScore: number;
}

const STATUS_COLORS: Record<string, string> = {
  AWAITING_REVIEW: 'bg-copper-soft text-copper-deep',
  UNDER_REVIEW: 'bg-copper-soft text-copper',
  AUTO_APPROVED: 'bg-success/10 text-success',
  APPROVED: 'bg-success/10 text-success',
  REJECTED: 'bg-danger/10 text-danger',
  SUBMITTED: 'bg-copper-soft text-copper-deep',
};

const VERDICT_COLORS: Record<string, string> = {
  AUTO_APPROVABLE: 'bg-success/10 text-success',
  NEEDS_REVIEW: 'bg-copper-soft text-copper-deep',
  INSUFFICIENT_EVIDENCE: 'bg-slate-100 text-slate-500',
  LIKELY_REJECT: 'bg-danger/10 text-danger',
};

// Mock chart data for age distribution
const AGE_DIST_DATA = [
  { label: '0-2d', count: 8 },
  { label: '3-5d', count: 14 },
  { label: '6-10d', count: 9 },
  { label: '11-20d', count: 5 },
  { label: '21d+', count: 3 },
];

function daysAgo(dateStr: string | null): number {
  if (!dateStr) return 0;
  const ms = Date.now() - new Date(dateStr).getTime();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

export function ApproverDashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  usePageTitle('approver.dashboard.title');

  const [kpis, setKpis] = useState<KpiData | null>(null);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [loadingKpis, setLoadingKpis] = useState(true);
  const [loadingQueue, setLoadingQueue] = useState(true);

  useEffect(() => {
    api.get<KpiData>('/approver/kpis/today')
      .then(setKpis)
      .catch(() => setKpis(null))
      .finally(() => setLoadingKpis(false));

    api.get<QueueItem[]>('/approver/queue')
      .then(setQueue)
      .catch(() => setQueue([]))
      .finally(() => setLoadingQueue(false));
  }, []);

  const topFive = queue.slice(0, 5);

  return (
    <div className="space-y-8">
      {/* Page header */}
      <div>
        <h1 className="font-serif italic text-3xl text-ink">
          {t('approver.dashboard.title', { defaultValue: 'Approver Dashboard' })}
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          {t('approver.dashboard.subtitle', {
            defaultValue: 'Real-time claims processing overview for Mondelez Indonesia.',
          })}
        </p>
      </div>

      {/* Speed KPI strip */}
      {location.pathname === '/approver' && <SpeedKpiStrip />}

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Awaiting review */}
        <button
          data-tour="awaiting-review-card"
          type="button"
          onClick={() => navigate('/approver/queue')}
          className="p-5 border border-copper/30 rounded bg-copper-soft text-left hover:shadow-card-hover transition-shadow group"
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-copper-deep mb-1">
            {t('approver.dashboard.awaitingReview', { defaultValue: 'Awaiting your review' })}
          </p>
          <p className="font-serif italic text-4xl text-copper">
            {loadingKpis ? '—' : (kpis?.awaitingReview ?? 0)}
          </p>
          <p className="text-xs text-copper-deep mt-2 group-hover:underline">
            {t('approver.dashboard.viewQueue', { defaultValue: 'View queue →' })}
          </p>
        </button>

        {/* Auto-approved today */}
        <button
          data-tour="auto-approved-card"
          type="button"
          onClick={() => navigate('/approver/auto-approved')}
          className="p-5 border border-success/20 rounded bg-success/5 text-left hover:shadow-card-hover transition-shadow group"
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-success mb-1">
            {t('approver.dashboard.autoApprovedToday', { defaultValue: 'Auto-approved today' })}
          </p>
          <p className="font-serif italic text-4xl text-success">
            {loadingKpis ? '—' : (kpis?.autoApprovedToday ?? 0)}
          </p>
          <p className="text-xs text-slate-400 mt-2 group-hover:underline">
            {t('approver.dashboard.viewAutoApproved', { defaultValue: 'Spot-check →' })}
          </p>
        </button>

        {/* Settled this week */}
        <button
          type="button"
          onClick={() => navigate('/approver/settled')}
          className="p-5 border border-hairline rounded bg-bone text-left hover:shadow-card-hover transition-shadow group"
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
            {t('approver.dashboard.settledThisWeek', { defaultValue: 'Settled this week' })}
          </p>
          <p className="font-serif italic text-4xl text-ink">
            {loadingKpis ? '—' : (kpis?.settledThisWeek ?? 0)}
          </p>
          <p className="text-xs text-slate-400 mt-2 group-hover:underline">
            {t('approver.dashboard.viewSettled', { defaultValue: 'View settlements →' })}
          </p>
        </button>

        {/* External verification pending */}
        {(kpis?.awaitingExternalVerification ?? 0) > 0 && (
          <button
            type="button"
            onClick={() => navigate('/approver/external-verifications')}
            className="p-5 border border-amber-300 rounded bg-amber-50 text-left hover:shadow-card-hover transition-shadow group"
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-700 mb-1">
              {t('approver.externalVerification.title', { defaultValue: 'External Verification' })}
            </p>
            <p className="font-serif italic text-4xl text-amber-700">
              {loadingKpis ? '—' : (kpis?.awaitingExternalVerification ?? 0)}
            </p>
            <p className="text-xs text-amber-600 mt-2 group-hover:underline">
              {t('approver.externalVerification.resolve', { defaultValue: 'Resolve →' })}
            </p>
          </button>
        )}
      </div>

      {/* SLA performance chart */}
      <div className="border border-hairline rounded bg-bone p-5">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-4">
          {t('approver.dashboard.ageDist', { defaultValue: 'Claim age distribution (days pending)' })}
        </p>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={AGE_DIST_DATA} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(14,17,22,0.08)" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: '#6B7280' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#6B7280' }}
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
            />
            <Tooltip
              contentStyle={{
                background: '#F6F4EE',
                border: '1px solid rgba(14,17,22,0.10)',
                borderRadius: 4,
                fontSize: 12,
              }}
              labelStyle={{ fontWeight: 600, color: '#0E1116' }}
              formatter={(value) => [value, t('approver.dashboard.claims', { defaultValue: 'claims' })]}
            />
            <ReferenceLine y={10} stroke="#C2412C" strokeDasharray="4 2" strokeWidth={1} label={{ value: 'SLA risk', fontSize: 10, fill: '#C2412C' }} />
            <Bar dataKey="count" fill="#A86A2C" radius={[2, 2, 0, 0]} maxBarSize={48} />
          </BarChart>
        </ResponsiveContainer>
        <p className="text-xs italic text-slate-400 mt-2">
          {t('approver.dashboard.chartNote', {
            defaultValue: 'Based on queue snapshot. SLA target: 7 days.',
          })}
        </p>
      </div>

      {/* Queue snapshot */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t('approver.dashboard.queueSnapshot', { defaultValue: 'Queue snapshot' })}
          </p>
          <button
            type="button"
            onClick={() => navigate('/approver/queue')}
            className="text-xs text-copper hover:underline"
          >
            {t('approver.dashboard.viewFullQueue', { defaultValue: 'View full queue →' })}
          </button>
        </div>

        {loadingQueue ? (
          <div className="space-y-2 animate-pulse">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-12 bg-slate-100 rounded" />
            ))}
          </div>
        ) : topFive.length === 0 ? (
          <div className="py-8 text-center border border-hairline rounded bg-bone">
            <p className="text-sm text-slate-400">
              {t('approver.dashboard.queueEmpty', { defaultValue: 'No claims awaiting review.' })}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-hairline text-xs uppercase tracking-wide text-slate-500">
                  <th className="text-left pb-2 pr-4">
                    {t('claims.claimNumber')}
                  </th>
                  <th className="text-left pb-2 pr-4">
                    {t('approver.queue.distributor', { defaultValue: 'Distributor' })}
                  </th>
                  <th className="text-right pb-2 pr-4">
                    {t('claims.amount')}
                  </th>
                  <th className="text-left pb-2 pr-4">
                    {t('approver.queue.verdict', { defaultValue: 'AI Verdict' })}
                  </th>
                  <th className="text-left pb-2 pr-4">
                    {t('approver.queue.priority', { defaultValue: 'Priority' })}
                  </th>
                  <th className="text-right pb-2">
                    {t('approver.queue.age', { defaultValue: 'Age' })}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-hairline">
                {topFive.map((item) => {
                  const age = daysAgo(item.submittedAt);
                  return (
                    <tr
                      key={item.id}
                      onClick={() => navigate(`/approver/claims/${item.id}`)}
                      className="hover:bg-bone cursor-pointer transition-colors"
                    >
                      <td className="py-3 pr-4 font-mono text-xs text-slate-600">
                        {item.claimNumber}
                      </td>
                      <td className="py-3 pr-4 text-xs text-slate-600 max-w-[120px] truncate">
                        {item.distributorName}
                      </td>
                      <td className="py-3 pr-4 text-right font-mono text-xs">
                        {formatCurrency(Number(item.claimedAmount), item.isoCurrencyCode)}
                      </td>
                      <td className="py-3 pr-4">
                        {item.aiVerdict ? (
                          <span
                            className={[
                              'text-xs font-semibold px-2 py-0.5 rounded-sm',
                              VERDICT_COLORS[item.aiVerdict] ?? 'bg-slate-100 text-slate-500',
                            ].join(' ')}
                          >
                            {t(`wizard.verdicts.${item.aiVerdict}`, {
                              defaultValue: item.aiVerdict,
                            })}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        <PriorityBadge score={item.priorityScore} />
                      </td>
                      <td className="py-3 text-right text-xs font-mono text-slate-500">
                        {age}d
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// suppress unused
void formatDate;
void STATUS_COLORS;
